import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { environment } from '../../../../../environments/environment';
import { MONTHS } from '../../../../constants/agenda-view.constants';
import { DATE_FORMAT, DATE_ISO_FORMAT } from '../../../../constants/date.constants';
import { TRANSLATION_TEMPLATES } from '../../../../constants/translation-templates-constants';
import { AgendaFilteredData, EventDateDao } from '../../../../db-models/event-date-dao';
import { CustomBookingMessageTemplate } from '../../../../db-models/widget-conf-dao';
import { EventCartItem } from '../../../../models/cart.model';
import { EventState, UserState } from '../../../../models/state.model';
import { DateUtilsService } from '../../../../services/date-utils.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { WidgetUtilService } from '../../../../services/widget-util.service';
import { CalioMeetingTemplatePipe } from '../../../../shared/pipes/calio-meeting-template.pipe';
import { IsEmptyStringPipe } from '../../../../shared/pipes/is-empty-string.pipe';
import { KeepOrderKeyvaluePipe } from '../../../../shared/pipes/keep-order-keyvalue.pipe';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../../shared/pipes/trust-html.pipe';
import { CwCheckboxRadioButtonComponent } from '../../../../widget/common/theme/cw-checkbox-radio-button/cw-checkbox-radio-button.component';

const pipes = [KeepOrderKeyvaluePipe, TrustHtmlPipe, CalioMeetingTemplatePipe, IsEmptyStringPipe, DatePipe, TranslationPipe]
const components = [CwCheckboxRadioButtonComponent]
const modules = [TranslateModule, FontAwesomeModule]
const common = [NgClass, NgTemplateOutlet]

@Component({
  selector: 'app-event-agenda-view',
  imports: [...common, ...modules, ...pipes, ...components],
  templateUrl: './event-agenda-view.component.html',
  styleUrl: './event-agenda-view.component.scss'
})
export class EventAgendaViewComponent {

  @Input() eventState: EventState;
  @Input('eventOnYear') set EventForYear(eventforYear: EventDateDao[]) {
    this.selectedMonth = undefined;
    this._eventforYearWithoutFilter = eventforYear;
    this.prepareObject(eventforYear);
  };
  @Input({ transform: booleanAttribute }) calendarLoaded: boolean;
  @Input({ transform: booleanAttribute }) noFreeSlots: boolean;
  @Input() bookedItems: EventCartItem[];
  @Input() cart: EventCartItem[];
  @Input() selectedDate: string;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() lang: string;
  @Input() maxDate: string;
  @Input() currentDate: string;
  @Input() protected userState: UserState;

  @Output() updateCartEvent = new EventEmitter<any>();
  @Output() gotoNextPageEvent = new EventEmitter<any>();
  @Output() viewYearChangedEvent = new EventEmitter<{ isNext: boolean, isPrevious: boolean }>();

  private dateUtilsService = inject(DateUtilsService);
  private timezoneService = inject(TimezoneService);
  private widgetUtilService = inject(WidgetUtilService);

  protected readonly monthsConst = MONTHS;
  protected readonly dateFormat = DATE_FORMAT;
  private readonly dateIsoFormat = DATE_ISO_FORMAT;
  protected readonly environment = environment;
  protected readonly templateContent = TRANSLATION_TEMPLATES;

  protected get timeFormat(): string {
    return this.dateUtilsService.userTimeFormat.date;
  }

  protected get datePipeWithComma(): string {
    return this.dateUtilsService.userDateTimeWithCommaFormat;
  }

  protected get dateTimeFormat(): string {
    return this.dateUtilsService.userDateTimeFormat.date;
  }

  protected _eventforYear = {};
  protected _eventforYearWithoutFilter: EventDateDao[];
  protected selectedMonth: number;

  hideEventCapacity = this.widgetUtilService.getWidgetConf().hide_event_capacity;
  showWidgetSlotLocation = this.widgetUtilService.getWidgetConf().show_widget_slot_location;

  private prepareObject(slots: EventDateDao[]): void {
    const timezone = this.userState?.timezone || this.timezoneService.getLocalTimezone();
    const filteredData: AgendaFilteredData = {};

    slots.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    slots.forEach(event => {

      const monthIndex = moment.utc(event.start).tz(timezone).get('month') + 1;
      const eventDate = moment.utc(event.start).tz(timezone).format(DATE_ISO_FORMAT);

      if (!filteredData[monthIndex]) {
        filteredData[monthIndex] = {};
      }

      if (!filteredData[monthIndex][eventDate]) {
        filteredData[monthIndex][eventDate] = [];
      }

      filteredData[monthIndex][eventDate].push(event);
    });

    this._eventforYear = filteredData;
  }

  protected filterWithMonth(month: number): void {
    this.selectedMonth === month ? this.selectedMonth = null : this.selectedMonth = month;
  }

  protected getNextYearSlots(): void {
    this.viewYearChangedEvent.emit({ isNext: true, isPrevious: false });
  }

  protected getPreviousYearSlots(): void {
    this.viewYearChangedEvent.emit({ isNext: false, isPrevious: true });
  }

  // Event slot component code

  protected displayRegularData(event: EventDateDao): boolean {
    return (event.regular_booking_count < event.regular_capacity);
  }

  protected displayWaitingData(event: EventDateDao): boolean {
    return (event.waitinglist_booking_count < event.waitinglist_capacity);
  }

  protected isSlotBooked(slotId: number): boolean {
    for (const i in this.bookedItems) {
      if (this.bookedItems[i]['slotId'] === slotId) {
        return true;
      }
    }
    return false;
  }

  protected isTerminInCart(slotId: number): boolean {
    if (this.cart != null) {
      for (let i = this.cart.length - 1; i >= 0; i--) {
        if (this.cart[i].slotId === slotId) {
          return true;
        }
      }
    }
    return false;
  }

  protected updateCart(event: any, eventInfo: EventDateDao) {
    if (!this.displayRegularData(eventInfo)) {
      if (this.displayWaitingData(eventInfo)) {
        eventInfo.isWaitingList = true;
      }
    }

    this.updateCartEvent.emit({ event, eventInfo });
  }

  protected gotoNextPage(): void {
    this.gotoNextPageEvent.emit();
  }

  protected checkIfStartEndDatesAreEqual(startDate: Date, endDate: Date): boolean {
    return moment(moment(startDate).format(this.dateIsoFormat)).isSame(moment(moment(endDate).format(this.dateIsoFormat)));
  }
}
